import { useMemo, useEffect, useState } from "react";
import { useGetProductQuery } from "context/product/productApiSlice";
import { Link } from "react-router-dom";

import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Drawer from '@mui/material/Drawer';
import { Icon, IconButton, Card, Divider, Tooltip } from '@mui/material';

import MDTypography from "dashboard/components/MDTypography";
import MDButton from "dashboard/components/MDButton";
import MDBox from "dashboard/components/MDBox";
import MDAvatar from "dashboard/components/MDAvatar";
import loader from "assets/images/shapes/loader.svg";

import peopleShopping from "assets/images/illustrations/people-shopping-thumb.jpg";
import useCart from "hooks/useCart";
import useAlert from "hooks/useAlert";

export default function CartDrawer({anchor, state, toggleDrawer}) {
  const [productList, setProductList] = useState([]);
  const { cart, addItem, removeItem, cartTotal } = useCart();
  const {showAlerts} = useAlert();
  const attribute = useMemo(() =>({
      cart : JSON.stringify(cart.items.map(item=>item.id))
  }) ,[cart.items]);

  const {
    data: products = [],
    isLoading,
    isSuccess,
    isFetching,
    isError,
    error,
  } = useGetProductQuery(attribute, {
      // pollingInterval: 60000,
      refetchOnMountOrArgChange: true,
      skip : cart.items?.length === 0 
  });
  
  useEffect(()=>{
    if (isSuccess) {
      const { entities } = products;
      setProductList(Object.values(entities));
    }
    if(isError) {
      showAlerts({code:"error", icon:"error", title : "List des produit",msg : error.data?.error || "il y avait une erreur" , type : "toast"});
    }
  },[products, isSuccess, isError]);
  
  const onDelete = () => {
    showAlerts({code:"error", icon:"error", title : "Empty cart",msg : "Êtes-vous sûr de vouloir vider votre panier ?  ", type : "confirm", action: null}); 
  }
  
  useEffect(() => productList.length !== 0 && cart.items.length === 0 ? setProductList([]) : null, [setProductList, productList, cart.items]);

  const list = (anchors) => (
    <MDBox
      sx={{ p:.5, pt:8, overflow:"auto", width: anchors === 'top' || anchors === 'bottom' ? 'auto' : 360 }}
      role="presentation"
    > 
      {cart.items.length === 0 && (<MDBox textAlign="center" >
      <MDBox component="img" src={peopleShopping} sx={{maxWidth:"100%", objectFit:"cover"}} alt='shopping cart empty'/>
          <MDTypography display="block" color="secondary" variant="caption">Pick some products to fill up your shopping cart</MDTypography>
      </MDBox>)
      }
      <List>
        {productList.length && productList.map(product => (
          <Card key={product.id}  sx={{mt:.5, p: .5}}>
          <ListItem disablePadding>
              <MDAvatar variant="rounded"  imgType="product" src={`${process.env.REACT_APP_PUBLIC_URL}/img/product/${product.selectedImage}`} alt={product.productName} size="xl"  />
              <ListItemText sx={{p:.5}}>
                <MDTypography variant="h6">
                  {product.productName}
                  <MDTypography variant="caption" color='dark'> x{cart.items?.find(item => item.id === product.id)?.quantity}</MDTypography>
                  <MDTypography variant="body2" sx={{fontSize:'.75rem'}} color='dark'>{product.price.sold === 0 ? product.priceShow.original : product.priceShow.sold}</MDTypography>
                  </MDTypography>
                </ListItemText>
              <MDButton onClick={()=> removeItem({id:product.id, quantity:1})} disableElevation size="small" sx={{p:.5, minWidth:32, m:.5,mt:"auto"}} variant="gradient" color="secondary">
                <Icon sx={{mx:"auto"}} fontSize="lg">remove</Icon>
              </MDButton>
              <MDButton onClick={()=> addItem({id:product.id, quantity:1}) } disableElevation sx={{p:.5, minWidth:32, m:.5 ,mt:"auto"}} size="small" variant="gradient" color="secondary">
                <Icon sx={{mx:"auto"}} fontSize="lg">add</Icon>
              </MDButton>
              <IconButton onClick={()=>removeItem({id:product.id, quantity:0})} 
                    sx={{p:.5,m:0,position:"absolute",zIndex:9, backgroundColor:"rgba(0, 0, 0, 0.2)", top: 8, right:8 }}
                    size="medium"
                    color="secondary"
                    >
                <Icon sx={{mx:"auto"}} fontSize="small">delete_outlined</Icon>
              </IconButton>  
          </ListItem>
          </Card>
        ))} 

        {(isLoading || isFetching ) && <MDAvatar display="flex" sx={{padding: 0,position: "absolute",zIndex: 9,top: "50%",left: "50%",transform: "translate3d(-24px, -20px, 0)"}}  
                    src={loader} alt="Avatar" variant="square" size="md" />}

        <Divider/>
        <MDBox display="flex" justifyContent="flex-end" px={.5}>
            <MDTypography  color="dark" variant="subtitle1">Total : <MDTypography  color="primary" variant="body">{cartTotal(productList)}</MDTypography></MDTypography>
        </MDBox>
      </List>
      <Divider/>
      <MDBox textAlign="center">
        <MDButton component={Link} to='/Checkout/ShoppingCart' disableElevation size="medium" sx={{mr:1.5}} variant="gradient" color="primary">
          panier
        </MDButton>
        <MDButton component={Link} to='/Checkout/ShoppingCart/UserInfos' disableElevation size="medium"  variant="outlined" color="secondary">
          caisse
        </MDButton>
        </MDBox>
    </MDBox>
  );
  

  return (
          <Drawer
            anchor={anchor}
            open={state[anchor]}
            onClose={()=>toggleDrawer(anchor, false)}
            className="cart-drawer"
          >
            <MDBox  m={0} sx={{ position:"relative"}}>
              <MDBox display="flex" shadow="sm" justifyContent="space-between" sx={{backgroundColor:({palette})=>palette.grey[500], position:"absolute", width:"100%", zIndex:99}} p={1.5}>
              <MDTypography variant="h4">Shopping cart 
              {cart.items.length !== 0 && <Tooltip title="Vider le panier  ?" placement="top" arrow>
                <IconButton onClick={onDelete} 
                      sx={{p:.5,m:0,ml:1, backgroundColor:"rgba(0, 0, 0, 0.2)" }}
                      size="medium"
                      color="secondary"
                      >
                  <Icon sx={{mx:"auto"}} fontSize="small">delete_forever</Icon>
                </IconButton>
              </Tooltip>}
              </MDTypography>
              <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  onClick={()=>toggleDrawer(anchor, false)}
                  edge="end"
                  sx={{py:1, px:1.5,mr:1, alignSelf:"flex-end"}}
                  >
                <Icon>close</Icon>
              </IconButton>
              </MDBox>
            </MDBox>
            <Divider sx={{my:0}}/>
            {list(anchor)}
          </Drawer>
  );
}